import React, { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { flushSync } from 'react-dom'
import * as R from 'remeda'
import { useToastState } from '@react-stately/toast'
import { createContextWithHook } from '@chilipiper/utils'
import { AlertBannerType, ToastType } from './types'
import { ToastRegion } from './ToastRegion'

type State = {
  showAlertBanner: (alertBanner: Omit<AlertBannerType, 'INTERNAL_type'>, duration?: number) => void
  showToast: (toast: Omit<ToastType, 'INTERNAL_type'>, duration?: number) => void
}

const { useToastContext, ToastBaseProvider } = createContextWithHook<State>()('Toast')

export const useShowToast = () => useToastContext().showToast
export const useShowAlertBanner = () => useToastContext().showAlertBanner

type Props = {
  children: ReactNode
}

export const ToastProvider = ({ children }: Props) => {
  const state = useToastState<ToastType | AlertBannerType>({
    maxVisibleToasts: Infinity,
    wrapUpdate: wrapInViewTransition,
  })
  // If not added as ref we would need to put state in the useCallback dependency array which can cause infinite loops
  const add = useRef(state.add)
  useEffect(() => {
    add.current = state.add
  }, [state])

  const showToast = useCallback((props: Omit<ToastType, 'INTERNAL_type'>, duration = 3000) => {
    add.current({ ...props, INTERNAL_type: 'toast' }, { timeout: duration })
  }, [])
  const showAlertBanner = useCallback(
    (props: Omit<AlertBannerType, 'INTERNAL_type'>, duration = 3000) => {
      add.current(
        { ...props, INTERNAL_type: 'alert', isDismissable: props.isDismissable },
        { timeout: props.isDismissable ? duration : undefined }
      )
    },
    []
  )

  const value = useMemo(() => ({ showToast, showAlertBanner }), [showToast, showAlertBanner])

  return (
    <ToastBaseProvider value={value}>
      {children}
      {state.visibleToasts.length > 0 && <ToastRegion state={state} />}
    </ToastBaseProvider>
  )
}

const wrapInViewTransition = <R,>(fn: () => R): R => {
  if ('startViewTransition' in document) {
    let result: R
    document
      .startViewTransition(() => {
        // This is needed to use viewTransition with react
        // eslint-disable-next-line @eslint-react/dom/no-flush-sync
        flushSync(() => {
          result = fn()
        })
      })
      .ready.catch(R.doNothing())
    // @ts-expect-error - Callback in viewTransition and flushSync are synchronous
    return result
  } else {
    return fn()
  }
}
